/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/lib/api/websocket.ts
 * DESCRIPTION: WebSocket service for real-time telemetry streaming.
 *              Placeholder for future implementation.
 *
 * TODO: Implement when backend WebSocket endpoint (/v1/telemetry/stream)
 *       is ready. This service will handle:
 *       - Connection management with auto-reconnect
 *       - Message parsing and type safety
 *       - Event emission for React components
 *       - Authentication via API key + Clerk JWT
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-04-08
 * ─────────────────────────────────────────────────────────────────────
 */

import type { WebSocketConfig, WebSocketMessage } from './types';

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectAttempts = 0;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;

  constructor(config: WebSocketConfig) {
    this.config = {
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
      ...config,
    };
  }

  /**
   * Connect to WebSocket endpoint
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Build URL with auth params
        const url = new URL(this.config.url);
        url.searchParams.append('api_key', this.config.apiKey);
        if (this.config.authToken) {
          url.searchParams.append('auth_token', this.config.authToken);
        }

        this.ws = new WebSocket(url.toString());

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          this.config.onConnect?.();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.config.onMessage?.(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.config.onError?.(new Error(`WebSocket error: ${JSON.stringify(error)}`));
          reject(error);
        };

        this.ws.onclose = () => {
          console.log('WebSocket disconnected');
          this.stopHeartbeat();
          this.config.onDisconnect?.();
          this.attemptReconnect();
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.stopHeartbeat();
    this.reconnectAttempts = this.config.maxReconnectAttempts || 10; // Prevent auto-reconnect
  }

  /**
   * Send message to server
   */
  send(message: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, message not sent');
    }
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.send({ type: 'heartbeat', timestamp: new Date().toISOString() });
    }, 30000); // 30 second heartbeat
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= (this.config.maxReconnectAttempts || 10)) {
      console.error('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.config.reconnectInterval! * Math.pow(1.5, this.reconnectAttempts - 1),
      30000 // Cap at 30 seconds
    );

    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    setTimeout(() => this.connect(), delay);
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Singleton instance (optional)
let webSocketInstance: WebSocketService | null = null;

export const initializeWebSocket = (config: WebSocketConfig): WebSocketService => {
  webSocketInstance = new WebSocketService(config);
  return webSocketInstance;
};

export const getWebSocket = (): WebSocketService | null => {
  return webSocketInstance;
};
